import {
  Box,
  Button,
  Card,
  CardActionArea,
  Stack,
  Typography,
  Skeleton,
  alpha,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { type PopularAyah } from '../lib/api'
import { _fetchPopularShared } from './PopularSurahsSection'
import { getSurahInfo } from '../lib/surahs'
import { useOnlineStatus } from '../hooks/useOnlineStatus'

interface PopularAyahsSectionProps {
  /** How many to fetch (and render). Defaults to 10. */
  limit?: number
}

/**
 * Discover — top-N ayahs by bookmark count. Same disposition as popular
 * surahs: featured #1 paper card + compact grid for the rest. Click →
 * /reader/<n>?ayah=<a>.
 */
export function PopularAyahsSection({ limit = 10 }: PopularAyahsSectionProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [items, setItems] = useState<PopularAyah[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const online = useOnlineStatus()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    _fetchPopularShared(limit, limit)
      .then((resp) => {
        if (cancelled) return
        setItems(resp.ayahs ?? [])
      })
      .catch((err) => {
        if (cancelled) return
        if (import.meta.env.DEV) console.warn('PopularAyahsSection: fetch failed', err)
        setError(true)
        setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [limit])

  if (error && !online) return null // offline: hide the section instead of an error card

  const hasItems = !!items && items.length > 0
  const featured = hasItems ? items![0] : null
  const rest = hasItems ? items!.slice(1) : []

  const openAyah = (surah: number, ayah: number) =>
    navigate(`/reader/${surah}?ayah=${ayah}`)

  return (
    <Box sx={{ mb: 3.5 }}>
      <Stack spacing={0.35} sx={{ mb: 1.75 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <StarIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            {t('discover.popularAyahs')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ pl: { xs: 0, sm: 4 } }}>
          {t('discover.popularAyahsHint')}
        </Typography>
      </Stack>

      {loading ? (
        <Stack spacing={1.25}>
          <Skeleton variant="rounded" sx={{ height: 120, borderRadius: 2.5 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 1.25,
            }}
          >
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" sx={{ height: 88, borderRadius: 2.5 }} />
            ))}
          </Box>
        </Stack>
      ) : !hasItems ? (
        <Box
          sx={{
            borderRadius: 2.5,
            border: 1,
            borderColor: 'divider',
            borderStyle: 'dashed',
            bgcolor: 'background.paper',
            p: 2.5,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {error ? t('discover.popularError') : t('discover.popularAyahsEmpty')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {featured && (
            <FeaturedAyahCard
              item={featured}
              rank={1}
              surahName={
                getSurahInfo(featured.surahNumber)?.englishName
                ?? t('playlists.surahFallback', { number: featured.surahNumber })
              }
              bookmarksLabel={t('discover.bookmarks', { count: featured.count })}
              openLabel={t('discover.open')}
              ariaLabel={t('discover.openAyah', {
                surah: featured.surahNumber,
                ayah: featured.ayahNumber,
                name: getSurahInfo(featured.surahNumber)?.englishName ?? '',
              })}
              onOpen={() => openAyah(featured.surahNumber, featured.ayahNumber)}
            />
          )}

          {rest.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 1.25,
              }}
            >
              {rest.map((item, idx) => {
                const info = getSurahInfo(item.surahNumber)
                const rank = idx + 2
                return (
                  <GridAyahCard
                    key={`${item.surahNumber}-${item.ayahNumber}`}
                    rank={rank}
                    surahName={
                      info?.englishName
                      ?? t('playlists.surahFallback', { number: item.surahNumber })
                    }
                    refLabel={`${item.surahNumber}:${item.ayahNumber}`}
                    bookmarksLabel={t('discover.bookmarks', { count: item.count })}
                    ariaLabel={t('discover.openAyah', {
                      surah: item.surahNumber,
                      ayah: item.ayahNumber,
                      name: info?.englishName ?? '',
                    })}
                    onOpen={() => openAyah(item.surahNumber, item.ayahNumber)}
                  />
                )
              })}
            </Box>
          )}
        </Stack>
      )}
    </Box>
  )
}

interface FeaturedAyahCardProps {
  item: PopularAyah
  rank: number
  surahName: string
  bookmarksLabel: string
  openLabel: string
  ariaLabel: string
  onOpen: () => void
}

function FeaturedAyahCard({
  item,
  rank,
  surahName,
  bookmarksLabel,
  openLabel,
  ariaLabel,
  onOpen,
}: FeaturedAyahCardProps) {
  return (
    <Card
      sx={{
        overflow: 'hidden',
        borderColor: (th) => alpha(th.palette.primary.main, 0.22),
      }}
    >
      <Box sx={{ display: 'flex', minHeight: 120 }}>
        <Box
          aria-hidden
          sx={{
            width: 5,
            flexShrink: 0,
            bgcolor: 'primary.main',
          }}
        />
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 2.25 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            gap: 2,
            background: (th) =>
              `linear-gradient(135deg, ${alpha(th.palette.primary.main, th.palette.mode === 'dark' ? 0.14 : 0.06)} 0%, transparent 70%)`,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 800,
              fontSize: 14,
            }}
            aria-hidden
          >
            #{rank}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.25 }}>
              {surahName}{' '}
              <Typography component="span" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                {item.surahNumber}:{item.ayahNumber}
              </Typography>
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.75, color: 'text.secondary' }}>
              <BookmarkIcon sx={{ fontSize: 15 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {bookmarksLabel}
              </Typography>
            </Stack>
          </Box>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            onClick={onOpen}
            aria-label={ariaLabel}
            sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, fontWeight: 700, flexShrink: 0 }}
          >
            {openLabel}
          </Button>
        </Box>
      </Box>
    </Card>
  )
}

interface GridAyahCardProps {
  rank: number
  surahName: string
  refLabel: string
  bookmarksLabel: string
  ariaLabel: string
  onOpen: () => void
}

function GridAyahCard({
  rank,
  surahName,
  refLabel,
  bookmarksLabel,
  ariaLabel,
  onOpen,
}: GridAyahCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea
        onClick={onOpen}
        aria-label={ariaLabel}
        sx={{
          height: '100%',
          p: 1.75,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          justifyContent: 'flex-start',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'primary.main',
            fontSize: '0.85rem',
            lineHeight: 1.4,
            minWidth: 28,
          }}
        >
          #{rank}
        </Typography>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }} noWrap>
            {surahName}{' '}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {refLabel}
            </Typography>
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.75, color: 'text.secondary' }}>
            <BookmarkIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {bookmarksLabel}
            </Typography>
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  )
}
